import type { RotatingGroup, RotatingPlayer, RotatingSet, SessionMatchPlan, WebSessionScoreDraftPayload } from '@tark/shared/lib/rotatingSets';
import {scoreError} from './rotatingScoreCorrection';

const sideKey=(side:RotatingPlayer[])=>side.map(p=>`${p.kind}:${p.userId??p.athleteId??p.displayName.trim().toLowerCase()}`).sort().join('|');
export const sameTeams=(a:RotatingSet,b:RotatingSet)=>
  sideKey(a.sideA)===sideKey(b.sideA)&&sideKey(a.sideB)===sideKey(b.sideB)
  ||sideKey(a.sideA)===sideKey(b.sideB)&&sideKey(a.sideB)===sideKey(b.sideA);
export function plansForGroup(group:RotatingGroup):SessionMatchPlan[] {
  return group.matches?.length ? group.matches.map(m=>({matchKey:m.matchKey,practice:group.practice,setOrdinals:m.sets.map(s=>s.ordinal)}))
    :group.sets.map(s=>({matchKey:s.ordinal,practice:group.practice,setOrdinals:[s.ordinal]}));
}
export function matchSets(sets:RotatingSet[],plan:SessionMatchPlan):RotatingSet[] {
  return plan.setOrdinals.map(n=>{const set=sets.find(s=>s.ordinal===n);if(!set)throw new Error('A saved set is missing.');return set;});
}
export function orientedMatch(sets:RotatingSet[]):RotatingSet[] {
  const first=sets[0];
  return sets.map(set=>sideKey(first.sideA)===sideKey(set.sideA)?set:{...set,
    sideA:set.sideB,sideB:set.sideA,gamesA:set.gamesB,gamesB:set.gamesA,tiebreakA:set.tiebreakB,tiebreakB:set.tiebreakA});
}
export function sessionMatchesError(sets:RotatingSet[],plans:SessionMatchPlan[]):string|null {
  if(!plans.length||plans.length>30)return 'Record one to thirty matches.';
  let reference=1;
  for(let i=0;i<plans.length;i++) {
    const plan=plans[i];
    if(!plan.setOrdinals.length||plan.setOrdinals.length>3)return `Match ${i+1}: use one to three sets.`;
    if(plan.setOrdinals.some(n=>n!==reference++))return 'Check the match and set order.';
    const grouped=matchSets(sets,plan);
    if(grouped.some(s=>[...s.sideA,...s.sideB].some(p=>p.draftMissing)))return `Match ${i+1}: choose every player.`;
    if(grouped.some(s=>{const ids=[...s.sideA,...s.sideB].map(p=>`${p.kind}:${p.userId??p.athleteId??p.displayName.toLowerCase()}`);return new Set(ids).size!==ids.length;}))
      return `Match ${i+1}: choose each player once.`;
    if(grouped.some(s=>!sameTeams(grouped[0],s)))return `Match ${i+1}: teams changed. Start another match.`;
    const scores=orientedMatch(grouped);
    let a=0,b=0;
    for(let j=0;j<scores.length;j++) {
      const set=scores[j],error=scoreError(set);
      if(error)return `Match ${i+1}, Set ${j+1}: ${error}`;
      if(a===2||b===2)return `Match ${i+1} already ended. Split the extra set into another match.`;
      if(scores.length>1&&set.format==='TB10'&&(j!==2||a!==1||b!==1))return 'A deciding tiebreak follows two split sets.';
      if(set.gamesA!>set.gamesB!)a++;else b++;
    }
    if(scores.length>1&&Math.max(a,b)!==2)return `Match ${i+1}: add the decider or save a draft.`;
  }
  return reference===sets.length+1?null:'Assign every set to a match.';
}
export function toWebDraft(group:RotatingGroup,sets:RotatingSet[],matches:SessionMatchPlan[]):WebSessionScoreDraftPayload {
  const player=(p:RotatingPlayer)=>p.draftMissing?null:({kind:p.kind,name:p.displayName,...(p.kind==='USER'?{userId:p.userId!}
    :p.kind==='CHILD'?{athleteId:p.athleteId!}:{guestName:p.displayName,adultConfirmed:p.adultConfirmed===true})});
  return {slots:sets[0].sideA.length===1?1:2,practice:group.practice,chosenWinner:null,expectedResultRevision:group.revision,matches,
    sets:sets.map(s=>({sideA:s.sideA.map(player),sideB:s.sideB.map(player),gamesA:s.gamesA==null?'':String(s.gamesA),
      gamesB:s.gamesB==null?'':String(s.gamesB),tiebreakA:s.tiebreakA==null?'':String(s.tiebreakA),
      tiebreakB:s.tiebreakB==null?'':String(s.tiebreakB),format:s.format??'SIX_GAME'}))};
}
export function fromWebDraft(payload:WebSessionScoreDraftPayload,group:RotatingGroup):RotatingSet[] {
  if(payload.expectedResultRevision!==group.revision||!Array.isArray(payload.sets)||!payload.sets.length||payload.sets.length>90)
    throw new Error('The saved result changed. Start a new correction.');
  const player=(p:WebSessionScoreDraftPayload['sets'][number]['sideA'][number]):RotatingPlayer=>{
    if(p==null) return {kind:'USER',userId:null,athleteId:null,displayName:'Choose player',draftMissing:true};
    return {kind:p.kind,userId:p.userId??null,athleteId:p.athleteId??null,displayName:p.name,
      ...(p.kind==='GUEST'?{adultConfirmed:p.adultConfirmed===true}:{})};
  };
  const number=(v:string)=>v===''?null:/^\d{1,2}$/.test(v)?Number(v):null;
  return payload.sets.map((s,i)=>({ordinal:i+1,revision:group.revision,sideA:s.sideA.map(player),sideB:s.sideB.map(player),
    gamesA:number(s.gamesA),gamesB:number(s.gamesB),tiebreakA:number(s.tiebreakA),tiebreakB:number(s.tiebreakB),
    format:s.format,outcome:'UNRECORDED',changedAt:'',correctionReason:null}));
}
